import React, { useEffect, useRef, useMemo } from 'react';
import { Message, MessageSource, FollowUpAction, SourceType } from '../types/chat';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { cn } from '../lib/utils';
import { Bot, User, ExternalLink, FileText, Mail, MessageSquare, Folder, TrendingUp, ArrowRight, Bell } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

interface ChatMessagesProps {
  messages: Message[];
  className?: string;
}

export const ChatMessages: React.FC<ChatMessagesProps> = ({ messages, className }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className={cn("flex items-center justify-center h-full", className)}>
        <div className="text-center">
          <p className="text-muted-foreground text-lg">
            Start a conversation by sending a message
          </p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className={cn("h-full", className)}>
      <div className="flex flex-col gap-4 p-4 max-w-4xl mx-auto" ref={scrollRef}>
        {messages.map((message) => (
          <div
            key={message.id}
            className={cn(
              "flex gap-3",
              message.role === 'user' ? 'justify-end' : 'justify-start'
            )}
          >
            {message.role === 'assistant' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
            )}

            <div
              className={cn(
                "rounded-lg px-4 py-3 max-w-[80%]",
                message.role === 'user'
                  ? 'bg-primary text-primary-foreground'
                  : message.isContacting
                    ? 'bg-muted/50 text-muted-foreground border border-dashed border-border'
                    : 'bg-muted text-muted-foreground'
              )}
            >
              {/* Assistant Response Indicator - shows when response is from a specific person's assistant */}
              {message.role === 'assistant' && message.mentionedPerson && !message.isContacting && (
                <div className="mb-3 pb-2 border-b border-border/50">
                  <div className="flex items-center gap-2">
                    <Bot className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-medium text-foreground">
                      Response from {message.mentionedPerson}'s assistant
                    </span>
                  </div>
                </div>
              )}

              {/* Service Logos Bar - only show logos for services used in sources - at the top */}
              {message.role === 'assistant' && message.sources && message.sources.length > 0 && (() => {
                const uniqueSourceTypes = Array.from(new Set(message.sources.map(s => s.type)));
                const serviceLogos = uniqueSourceTypes
                  .filter(type => ['sharepoint', 'teams', 'onedrive', 'email'].includes(type))
                  .map(type => {
                    switch (type) {
                      case 'sharepoint':
                        return <SharePointLogo key="sharepoint" className="h-4 w-4" />;
                      case 'teams':
                        return <TeamsLogo key="teams" className="h-4 w-4" />;
                      case 'onedrive':
                        return <OneDriveLogo key="onedrive" className="h-4 w-4" />;
                      case 'email':
                        return <EmailLogo key="email" className="h-4 w-4 text-blue-600 dark:text-blue-400" />;
                      default:
                        return null;
                    }
                  })
                  .filter(Boolean);

                return serviceLogos.length > 0 ? (
                  <div className="mb-3 pb-3 border-b border-border/50">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">
                        Search results from sources
                      </span>
                      {serviceLogos}
                    </div>
                  </div>
                ) : null;
              })()}

              {message.isContacting ? (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                  <p className="text-sm italic text-muted-foreground">
                    {message.content}
                  </p>
                </div>
              ) : (
                <div className="text-sm whitespace-pre-wrap break-words">
                  <ReactMarkdown
                    components={{
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
                      ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
                      li: ({ children }) => <li className="ml-2">{children}</li>,
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              )}

              {/* Human Notification Indicator */}
              {message.role === 'assistant' && message.humanNotified && message.notifiedPerson && (
                <div className="mt-4 pt-4 border-t border-border/50">
                  <div className="flex items-start gap-3 p-3 rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800">
                    <Bell className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        {message.notifiedPerson} has been notified
                      </p>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        You'll receive a direct response when available
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Confidence Level - only show if there's an actual answer */}
              {message.role === 'assistant' && message.confidenceLevel !== undefined &&
                !message.content.toLowerCase().includes("can't answer this question confidently") && (
                  <div className={cn(
                    "mt-4 pt-4 border-t border-border/50",
                    message.humanNotified && "mt-3"
                  )}>
                    <div className="flex items-start gap-3">
                      <TrendingUp className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2.5 flex-wrap">
                          <span className="text-sm font-medium text-muted-foreground">Confidence Level:</span>
                          <span className={cn(
                            "text-sm font-semibold",
                            message.confidenceLevel >= 80 ? 'text-green-600 dark:text-green-400' :
                              message.confidenceLevel >= 60 ? 'text-yellow-600 dark:text-yellow-400' :
                                'text-orange-600 dark:text-orange-400'
                          )}>
                            {message.confidenceLevel >= 80 ? 'High' : message.confidenceLevel >= 60 ? 'Medium' : 'Low'} ({message.confidenceLevel}%)
                          </span>
                        </div>
                        {message.confidenceLevel >= 80 && (
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            — KPIs sourced from approved strategic planning and AI governance documents.
                          </p>
                        )}
                        {message.confidenceLevel >= 60 && message.confidenceLevel < 80 && message.content.includes('Organization-published policies') && (
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            — Knowledge Scope: Organization-published policies
                          </p>
                        )}
                        {message.confidenceLevel >= 90 && message.content.includes('Owner-verified sources') && (
                          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                            — Knowledge Scope: Owner-verified sources (Raza)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}


              {/* Sources for assistant messages */}
              {message.role === 'assistant' && message.sources && message.sources.length > 0 && (
                <div className={cn(
                  "mt-4 pt-4 border-t border-border/50",
                  message.confidenceLevel !== undefined && "mt-3"
                )}>
                  <p className="text-sm font-medium mb-3 text-muted-foreground">Sources:</p>
                  <CategorizedSources sources={message.sources} />
                </div>
              )}

              {/* Follow-up Actions */}
              {message.role === 'assistant' && message.followUpActions && message.followUpActions.length > 0 && (
                <div className="mt-4 pt-4 border-t border-border/50">
                  <p className="text-sm font-medium mb-3 text-muted-foreground">Follow-up Actions:</p>
                  <div className="flex flex-col gap-2">
                    {message.followUpActions.map((action) => (
                      <FollowUpActionItem key={action.id} action={action} />
                    ))}
                  </div>
                </div>
              )}

              <p className={cn(
                "text-xs mt-1",
                message.role === 'user'
                  ? 'text-primary-foreground/70'
                  : 'text-muted-foreground'
              )}>
                {message.timestamp.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            {message.role === 'user' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <User className="h-4 w-4 text-primary-foreground" />
              </div>
            )}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
};

// Categorize sources into groups
type SourceCategory = 'knowledge-base' | 'communication' | 'assistants';

interface CategorizedSourcesProps {
  sources: MessageSource[];
}

const CategorizedSources: React.FC<CategorizedSourcesProps> = ({ sources }) => {
  const categorized = useMemo(() => {
    const categories: Record<SourceCategory, MessageSource[]> = {
      'knowledge-base': [],
      'communication': [],
      'assistants': [],
    };

    sources.forEach(source => {
      if (source.type === 'sharepoint' || source.type === 'onedrive') {
        categories['knowledge-base'].push(source);
      } else if (source.type === 'teams' || source.type === 'email') {
        categories['communication'].push(source);
      } else if (source.type === 'human-assistant' || source.type === 'assistant') {
        categories['assistants'].push(source);
      } else {
        // Default to knowledge-base for unknown types
        categories['knowledge-base'].push(source);
      }
    });

    return categories;
  }, [sources]);

  const getCategoryLabel = (category: SourceCategory): string => {
    switch (category) {
      case 'knowledge-base':
        return 'Knowledge Base';
      case 'communication':
        return 'Communication Channels';
      case 'assistants':
        return 'Assistants';
      default:
        return 'Other';
    }
  };

  const hasAnySources = Object.values(categorized).some(sources => sources.length > 0);

  if (!hasAnySources) return null;

  return (
    <Accordion type="multiple" className="w-full">
      {categorized['knowledge-base'].length > 0 && (
        <AccordionItem value="knowledge-base" className="border-none">
          <AccordionTrigger className="py-2 text-xs font-medium text-muted-foreground hover:no-underline">
            {getCategoryLabel('knowledge-base')} ({categorized['knowledge-base'].length})
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <div className="flex flex-col gap-2">
              {categorized['knowledge-base'].map((source) => (
                <MessageSourceItem key={source.id} source={source} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {categorized['communication'].length > 0 && (
        <AccordionItem value="communication" className="border-none">
          <AccordionTrigger className="py-2 text-xs font-medium text-muted-foreground hover:no-underline">
            {getCategoryLabel('communication')} ({categorized['communication'].length})
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <div className="flex flex-col gap-2">
              {categorized['communication'].map((source) => (
                <MessageSourceItem key={source.id} source={source} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}

      {categorized['assistants'].length > 0 && (
        <AccordionItem value="assistants" className="border-none">
          <AccordionTrigger className="py-2 text-xs font-medium text-muted-foreground hover:no-underline">
            {getCategoryLabel('assistants')} ({categorized['assistants'].length})
          </AccordionTrigger>
          <AccordionContent className="pb-2">
            <div className="flex flex-col gap-2">
              {categorized['assistants'].map((source) => (
                <MessageSourceItem key={source.id} source={source} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      )}
    </Accordion>
  );
};

// Logo components for different services - using official Microsoft brand colors
const SharePointLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#0078D4" />
    <path d="M2 12l10 5 10-5" fill="#0078D4" opacity="0.85" />
    <path d="M2 17l10 5 10-5" fill="#0078D4" opacity="0.7" />
  </svg>
);

const TeamsLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="4" y="6" width="14" height="11" rx="1" fill="#6264A7" />
    <rect x="18" y="4" width="4" height="15" rx="1" fill="#464EB8" />
    <circle cx="8" cy="11.5" r="1.5" fill="white" />
  </svg>
);

const OneDriveLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12.5 7.5c-1.1 0-2 .9-2 2s.9 2 2 2h7c1.1 0 2-.9 2-2s-.9-2-2-2c-.2-1.1-1.1-2-2.2-2-1.1 0-2 .9-2.2 2H12.5z" fill="#0078D4" />
    <path d="M19.5 9.5c1.1 0 2 .9 2 2s-.9 2-2 2h-7c-1.1 0-2-.9-2-2s.9-2 2-2h7z" fill="#28A8EA" />
  </svg>
);

const EmailLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

// Source item component
const MessageSourceItem: React.FC<{ source: MessageSource }> = ({ source }) => {
  const getLogo = () => {
    const logoClass = "h-4 w-4 flex-shrink-0";
    switch (source.type) {
      case 'onedrive':
        return <OneDriveLogo className={logoClass} />;
      case 'sharepoint':
        return <SharePointLogo className={logoClass} />;
      case 'email':
        return <EmailLogo className={cn(logoClass, "text-blue-600 dark:text-blue-400")} />;
      case 'teams':
        return <TeamsLogo className={logoClass} />;
      case 'assistant':
        return <MessageSquare className={cn(logoClass, "text-purple-500 dark:text-purple-400")} />;
      case 'human-assistant':
        return <Bot className={cn(logoClass, "text-indigo-500 dark:text-indigo-400")} />;
      default:
        return <ExternalLink className={cn(logoClass, "text-muted-foreground")} />;
    }
  };

  const getServiceName = () => {
    switch (source.type) {
      case 'onedrive':
        return 'OneDrive';
      case 'sharepoint':
        return 'SharePoint';
      case 'email':
        return 'Email';
      case 'teams':
        return 'Teams';
      case 'assistant':
        return 'Assistant';
      case 'human-assistant':
        return "Human's Assistant";
      default:
        return null;
    }
  };

  const hasServiceLogo = ['onedrive', 'sharepoint', 'teams', 'email', 'assistant', 'human-assistant'].includes(source.type);
  const serviceName = getServiceName();

  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-start gap-3 p-2.5 rounded-md hover:bg-accent/50 transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-foreground group-hover:text-primary">
            {source.title}
          </p>
          {hasServiceLogo && serviceName && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {getLogo()}
              <span className="text-xs text-muted-foreground font-medium">
                {serviceName}
              </span>
            </div>
          )}
        </div>
        {source.description && (
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
            {source.description}
          </p>
        )}
      </div>
      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground flex-shrink-0 mt-1" />
    </a>
  );
};

// Follow-up action component
const FollowUpActionItem: React.FC<{ action: FollowUpAction }> = ({ action }) => {
  if (action.href) {
    return (
      <a
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 p-2 rounded-md hover:bg-accent/50 transition-colors group text-xs"
      >
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
        <span className="text-foreground group-hover:text-primary">{action.label}</span>
      </a>
    );
  }

  return (
    <button
      onClick={action.onClick}
      className="flex items-center gap-2 p-2 rounded-md hover:bg-accent/50 transition-colors group text-xs text-left"
    >
      <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
      <span className="text-foreground group-hover:text-primary">{action.label}</span>
    </button>
  );
};

